// src/components/QboCustomerLinkDialog.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  Typography,
  CircularProgress,
  Chip,
  Alert,
  Stack,
  Autocomplete,
} from "@mui/material";

// ✅ import your smart customer search exported from the file you shared
import { CustomerSearchSelect } from "../../pages/instances/sales/orders/InstanceOrders";
import { toast } from "react-toastify";

const QB_ICON =
  "https://cdn-1.webcatalog.io/catalog/quickbooks/quickbooks-icon-filled-256.webp?v=1757897011406";

  
export default function QboCustomerLinkDialog({
  open,
  onClose,
  instanceUniqueId,
  customerId, // optional preselected Elite customerId (from invoice summary)
  customerName = "", // optional hint to seed the QBO search
  onLinked, // callback when linking succeeds
  invoiceId
}) {
  const SERVER = process.env.REACT_APP_NODEJS_SERVER_URL;
  const headers = { headers: { accessToken: localStorage.getItem("accessToken") } };

  // ───────────────────────── Elite customers (from Node) ─────────────────────────
  const [customers, setCustomers] = useState([]);
  const [custLoading, setCustLoading] = useState(false);
  const [custError, setCustError] = useState("");
  const [selectedCustomerId, setSelectedCustomerId] = useState(customerId || "");

  const selectedCustomer = useMemo(
    () => customers.find((c) => String(c.id) === String(selectedCustomerId)) || null,
    [customers, selectedCustomerId]
  );

  // Load (or reload) customers when dialog opens
  useEffect(() => {
    if (!open) return;
    let cancel = false;

    (async () => {
      setCustError("");
      setCustLoading(true);
      try {
        const { data } = await axios.get(`${SERVER}/customers/get-customers`, {
          params: { instanceUniqueId, tab: "all", rpp: 100 },
          ...headers,
        });

        if (!cancel) {
          const list = data?.Results || data?.customers || data || [];
          let normalized = Array.isArray(list) ? list : [];

          const wantedId = String(customerId || "").trim();
          const hasWanted = wantedId
            ? normalized.some((c) => String(c?.id) === wantedId)
            : false;

          if (wantedId && !hasWanted) {
            try {
              const byId = await axios.get(`${SERVER}/customers/${wantedId}`, { ...headers });
              const fetched = byId?.data?.customer || null;
              if (fetched?.id) {
                normalized = [fetched, ...normalized];
              }
            } catch (byIdErr) {
              console.warn("Load selected customer by id failed", byIdErr?.response?.data || byIdErr?.message || byIdErr);
            }
          }

          const dedup = [];
          const seen = new Set();
          for (const c of normalized) {
            const key = String(c?.id || "").trim();
            if (!key || seen.has(key)) continue;
            seen.add(key);
            dedup.push(c);
          }

          setCustomers(dedup);
        }
      } catch (e) {
        console.error("Load customers failed", e);
        if (!cancel) {
          setCustomers([]);
          setCustError(
            e?.response?.data?.error ||
              "Failed to load customers. Please try again or refresh."
          );
        }
      } finally {
        if (!cancel) setCustLoading(false);
      }
    })();

    return () => {
      cancel = true;
    };
  }, [open, instanceUniqueId, SERVER, customerId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Reset selection when dialog opens
  useEffect(() => {
    if (!open) return;
    setSelectedCustomerId(customerId || "");
  }, [open, customerId]);

  // If no explicit customerId was provided, try to preselect by customerName hint.
  useEffect(() => {
    if (!open) return;
    if (selectedCustomerId) return;
    const hint = String(customerName || "").trim().toLowerCase();
    if (!hint || !customers.length) return;
    const pick = customers.find((c) => {
      const name = String(
        c?.businessName || `${c?.firstName ?? ""} ${c?.lastName ?? ""}`.trim()
      )
        .trim()
        .toLowerCase();
      return name && (name === hint || name.includes(hint) || hint.includes(name));
    });
    if (pick?.id) setSelectedCustomerId(pick.id);
  }, [open, selectedCustomerId, customerName, customers]);

  // ───────────────────────── QBO search ─────────────────────────
  const [qboQuery, setQboQuery] = useState(customerName || "");
  const [qboLoading, setQboLoading] = useState(false);
  const [qboError, setQboError] = useState("");
  const [qboOptions, setQboOptions] = useState([]);
  const [selectedQbo, setSelectedQbo] = useState(null);
  const debounceRef = useRef();

  // Seed QBO search when Elite customer changes
  useEffect(() => {
    if (!open) return;

    // reset selection + list + error each time the dialog re-opens
    setSelectedQbo(null);
    setQboOptions([]);
    setQboError("");

    // Prefer business name, then provided hint, then email for a precise match
    if (selectedCustomer?.businessName) {
      setQboQuery(selectedCustomer.businessName);
    } else if (customerName) {
      setQboQuery(customerName);
    } else if (selectedCustomer?.emailAddress) {
      setQboQuery(selectedCustomer.emailAddress);
    } else if (!selectedCustomerId) {
      setQboQuery(customerName || "");
    }
  }, [open, selectedCustomerId, selectedCustomer, customerName]);

  // Perform QBO lookup (debounced) once an Elite customer is chosen
  useEffect(() => {
    if (!open) return;
    if (!selectedCustomerId) return;
    const scopedInstance = String(instanceUniqueId || "").trim();
    if (!scopedInstance) {
      setQboLoading(false);
      setQboError("Instance context is not ready yet. Please wait a second and try again.");
      return;
    }

    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      try {
        setQboError("");
        setQboLoading(true);

        const trimmed = String(qboQuery || "").trim();
        const params = { instanceUniqueId: scopedInstance };
        // Only send ?q= when there are >= 2 non-space chars.
        // Otherwise, let backend return the top 100 results.
        if (trimmed.length >= 2) params.q = trimmed;

        const { data } = await axios.get(
          `${SERVER}/invoices/qbo/customers/search`,
          { ...headers, params }
        );

        const results = (data?.results || []).map((r) => ({
          id: r.id,
          label: r.displayName,
          email: r.email || "",
          company: r.companyName || "",
        }));

        setQboOptions(results);

        // (Optional) Auto-select exact displayName match to reduce clicks
        const exact = trimmed
          ? results.find(
              (r) => (r.label || "").toLowerCase() === trimmed.toLowerCase()
            )
          : null;
        if (exact) setSelectedQbo(exact);
      } catch (e) {
        console.error("QBO search failed", e?.response?.data || e);

        // Don’t surface QBO parser noise to the user
        const status = e?.response?.status;
        const raw =
          e?.response?.data?.error ||
          e?.response?.data?.Fault?.Error?.[0]?.Message ||
          "";
        const looksLikeParser = /parse|parser|QueryParserError/i.test(raw);
        if (status === 400 && /instanceUniqueId/i.test(raw)) {
          setQboError("Instance context is missing. Please refresh and try again.");
        } else {
          setQboError(looksLikeParser ? "" : raw || "QuickBooks search failed.");
        }
      } finally {
        setQboLoading(false);
      }
    }, 300);

    return () => clearTimeout(debounceRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [qboQuery, selectedCustomerId, open, instanceUniqueId, SERVER]);

  // ───────────────────────── Link & Create actions ─────────────────────────
  const [linking, setLinking] = useState(false);
  const [creating, setCreating] = useState(false);

  const alreadyLinked = !!(
    selectedCustomer?.qboCustomerId ||
    selectedCustomer?.qbo_ref_id ||
    selectedCustomer?.qboCustomer?.id
  );

  // After-success handler: call onLinked and then reload the page
  const finishSuccess = (payload) => {
    try {
      onLinked?.(payload);
    } catch (_) {
      // ignore callback errors and still close
    }
    onClose?.();
  };

  // Link selected Elite customer to selected QBO customer
  const handleLink = async () => {
    if (!selectedCustomerId || !selectedQbo?.id) return;
    try {
      setLinking(true);
  
      // 1) link Elite customer to QBO
      await axios.put(
        `${SERVER}/customers/${selectedCustomerId}/qbo/link`,
        { instanceUniqueId, qboId: selectedQbo.id, displayName: selectedQbo.label },
        headers
      );
  
     // 2) stamp the specific invoice so future summaries short-circuit
     if (invoiceId) {
       await axios.post(
         `${SERVER}/invoices/${invoiceId}/qbo/link`,
         { customerId: selectedCustomerId, qboCustomerId: selectedQbo.id },
         headers
       );
     }
  
      toast.success(`Connected ${selectedCustomer?.businessName || customerName || "customer"} to QuickBooks (${selectedQbo.label}).`);
      finishSuccess({
        customerId: selectedCustomerId,
        qboId: selectedQbo.id,
        qboDisplayName: selectedQbo.label,
      });
    } catch (e) {
      console.error("Link failed", e?.response?.data || e);
      toast.error(e?.response?.data?.error || "Failed to link customer to QuickBooks.");
    } finally {
      setLinking(false);
    }
  };
  

  // Create a new QBO customer from the selected Elite customer, then link
  const handleCreateInQbo = async () => {
    if (!selectedCustomer) return;

    const payload = {
      instanceUniqueId,
      displayName:
        selectedCustomer.businessName ||
        `${selectedCustomer.firstName ?? ""} ${selectedCustomer.lastName ?? ""}`.trim() ||
        "Unnamed Customer",
      companyName: selectedCustomer.businessName || "",
      givenName: selectedCustomer.firstName || "",
      familyName: selectedCustomer.lastName || "",
      primaryEmail: selectedCustomer.emailAddress || "",
      primaryPhone: selectedCustomer.phoneNumber || "",
    };

    try {
      setCreating(true);

      const { data } = await axios.post(
        `${SERVER}/invoices/qbo/customers`,
        payload,
        headers
      );

      const newQbo = data?.customer || data; // normalize
      if (!newQbo?.id) {
        throw new Error("QBO customer creation returned no id.");
      }

      await axios.put(
        `${SERVER}/customers/${selectedCustomer.id}/qbo/link`,
        {
          instanceUniqueId,
          qboId: newQbo.id,
          displayName:
            newQbo.displayName || newQbo.FullyQualifiedName || payload.displayName,
        },
        headers
      );

      if (invoiceId) {
          await axios.post(
            `${SERVER}/invoices/${invoiceId}/qbo/link`,
            { customerId: selectedCustomer.id, qboCustomerId: newQbo.id },
            headers
          );
        }

      toast.success(`Created and connected QuickBooks customer: ${newQbo.displayName || newQbo.FullyQualifiedName || payload.displayName}.`);
      finishSuccess({
        customerId: selectedCustomer.id,
        qboId: newQbo.id,
        qboDisplayName:
          newQbo.displayName || newQbo.FullyQualifiedName || payload.displayName,
      });
    } catch (e) {
      console.error("Create QBO customer failed", e?.response?.data || e);
      toast.error(
        e?.response?.data?.error ||
          "Failed to create QuickBooks customer."
      );
    } finally {
      setCreating(false);
    }
  };

  // ───────────────────────── UI ─────────────────────────
  const disableQboArea = !selectedCustomerId; // 👈 keeps QBO select disabled until Elite customer picked
  const showCreateCta =
    !!selectedCustomer && !qboLoading && (qboOptions.length === 0 || !selectedQbo);

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>Connect Customer to QuickBooks</DialogTitle>

      {/* allow the popup to render cleanly near the field */}
      <DialogContent dividers sx={{ overflow: "visible" }}>
        <Stack spacing={2}>
          {/* Customers fetched from Node */}
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
            <Typography variant="caption">Elite Customer</Typography>

            <CustomerSearchSelect
              customers={customers}
              value={selectedCustomerId || ""}
              onChange={(val) => setSelectedCustomerId(val)}
              label={
                custLoading ? "Loading customers…" : "Search by customer name, email, or phone"
              }
            />

            {custError ? <Alert severity="error">{custError}</Alert> : null}

            {customers.length === 0 && !custLoading && !custError ? (
              <Alert severity="info">
                No Elite customers found yet. Create a customer in Elite first.
              </Alert>
            ) : null}

            {selectedCustomer && alreadyLinked && (
              <Chip
                size="small"
                color="success"
                variant="outlined"
                label={`Already linked${
                  selectedCustomer?.qboCustomerId ? ` (#${selectedCustomer.qboCustomerId})` : ""
                }`}
                sx={{ alignSelf: "flex-start" }}
              />
            )}
          </Box>

          {/* QBO Customer (disabled until Elite customer picked) */}
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
            <Typography variant="caption">QuickBooks Customer</Typography>

            <Autocomplete
              // Let it portal so it won't be clipped
              fullWidth
              size="small"
              disabled={disableQboArea}
              options={qboOptions}
              value={selectedQbo}
              onChange={(_, val) => setSelectedQbo(val)}
              inputValue={qboQuery}
              onInputChange={(_, val, reason) => {
                if (reason === "reset") return;
                setQboQuery(val);
              }}
              loading={qboLoading}
              getOptionLabel={(opt) => (opt?.label ?? "").toString()}
              isOptionEqualToValue={(a, b) => String(a?.id) === String(b?.id)}
              loadingText="Searching…"
              noOptionsText={disableQboArea ? "Pick an Elite customer first" : (qboError ? "Search unavailable" : "No QuickBooks matches") }
              ListboxProps={{ style: { maxHeight: 320, paddingTop: 0, paddingBottom: 0 } }}
              slotProps={{
                popper: {
                  // keep it above the dialog content
                  sx: { zIndex: (theme) => theme.zIndex.modal + 1 },
                },
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder={
                    disableQboArea
                      ? "Pick an Elite customer first"
                      : "Search QuickBooks customers…"
                  }
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {qboLoading ? <CircularProgress size={18} /> : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
              renderOption={(props, option) => (
                <li {...props} key={option.id}>
                  <Box sx={{ display: "flex", flexDirection: "column" }}>
                    <Typography variant="body2">{option.label}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {option.email || option.company || "—"}
                    </Typography>
                  </Box>
                </li>
              )}
            />

            {qboError ? <Alert severity="error">{qboError}</Alert> : null}
            {!disableQboArea && !qboError ? (
              <Typography variant="caption" color="text.secondary">
                Prefilled from Elite customer as a search hint. Choose a QuickBooks customer from results.
              </Typography>
            ) : null}

            {!disableQboArea && showCreateCta && (
              <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                <Button
                  variant="outlined"
                  onClick={handleCreateInQbo}
                  disabled={creating}
                  startIcon={<img src={QB_ICON} alt="" style={{ width: 16, height: 16 }} />}
                >
                  {creating ? "Creating…" : "Create in QuickBooks"}
                </Button>
                <Typography variant="caption" color="text.secondary">
                  No matching QBO customer? Create one from this Elite account.
                </Typography>
              </Box>
            )}
          </Box>
        </Stack>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} variant="text">
          Cancel
        </Button>

        <Button
          onClick={handleLink}
          disabled={!selectedCustomerId || !selectedQbo || linking}
          variant="contained"
          startIcon={<img src={QB_ICON} alt="" style={{ width: 16, height: 16 }} />}
        >
          {linking ? "Connecting…" : "Connect"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
